import { isNil, uniqBy } from 'lodash';

export const prettifyArray = (
  arr: any[],
  separator: string = ', ',
  lastSeparator: string = ', '
): string => {
  if (arr.length > 1) {
    const others = arr.slice(0, -1);
    const [tail] = arr.slice(-1);
    return `${others.join(separator)}${lastSeparator}${tail}`;
  } else if (arr.length === 1) {
    return `${arr[0]}`;
  } else {
    return '';
  }
};

export const prettifyMoney = (amount?: number): string =>
  `€ ${isNil(amount) ? '―' : amount.toFixed(2)}`;

const units = ['k', 'm', 'b', 't'];

const _abbreviate = (number: number, decPlaces: number) => {
  const dec = Math.pow(10, decPlaces);
  for (let i = units.length - 1; i >= 0; i--) {
    const size = Math.pow(10, (i + 1) * 3);
    if (size <= number) {
      number = Math.round((number * dec) / size) / dec;
      if (number === 1000 && i < units.length - 1) {
        number = 1;
        i++;
      }
      number += units[i];
      break;
    }
  }
  return number;
};

const _dynamicDecPlaces = (number: number): number =>
  5 - (Math.floor(Math.max(0, Math.log10(number))) % 6);

export const abbreviateNumber = (number: number, decPlaces: number): string => {
  const abbreviatedNumber = _abbreviate(Math.abs(number), decPlaces || _dynamicDecPlaces(number));
  return number < 0 ? '-' + abbreviatedNumber : abbreviatedNumber;
};

export const capitalizeString = (string: string): string =>
  string.charAt(0).toUpperCase() + string.substring(1);

export const capitalizeWords = (string: string): string =>
  string.replace(/(^|[^a-zA-Z\u00C0-\u017F'])([a-zA-Z\u00C0-\u017F])/g, m => m.toUpperCase());

export const prettifyTime = (timeInSeconds: number) => {
  if (isNaN(timeInSeconds)) {
    return '00:00';
  }
  const hrs = Math.floor(timeInSeconds / 3600);
  const mins = Math.floor((timeInSeconds % 3600) / 60);
  const secs = Math.floor(timeInSeconds % 60);
  return hrs > 0
    ? `${hrs < 10 ? '0' : ''}${hrs}:${mins < 10 ? '0' : ''}${mins}:${
        secs < 10 ? '0' : ''
      }${secs.toFixed()}`
    : `${mins < 10 ? '0' : ''}${mins}:${secs < 10 ? '0' : ''}${secs.toFixed()}`;
};

export const caseInsensitiveUniqBy = (array: any[], iteratee: any => ?string): any[] => {
  if (!array || !(array.length > 0)) {
    return [];
  }
  const getItemValue = (item: any): string => (iteratee(item) || '').trim().toLowerCase();
  return uniqBy(array, getItemValue);
};

export const fuzzyMatch = (text: string, searchToken: string) => {
  const normalizedText = String(text)
    .trim()
    .toLowerCase();
  const normalizedSearchToken = String(searchToken)
    .trim()
    .toLowerCase();
  return normalizedText.includes(normalizedSearchToken);
};

export default {
  capitalizeString,
  capitalizeWords,
  abbreviateNumber,
  prettifyArray,
  prettifyMoney,
  prettifyTime,
  caseInsensitiveUniqBy,
  fuzzyMatch,
};
